import { LazyQueryHookOptions, QueryHookOptions } from '@apollo/client';

import {
  CampaignComponentsQuery,
  CampaignComponentsQueryVariables,
  CampaignFilter,
  CampaignFragment,
  CampaignsQuery,
  CampaignsQueryVariables,
  CampaignsWithCampaignComponentsQuery,
  CampaignsWithCampaignComponentsQueryVariables,
  OfferFilter,
  useCampaignComponentsQuery,
  useCampaignQuery,
  useCampaignsLazyQuery,
  useCampaignsWithCampaignComponentsQuery,
  useOfferQuery,
} from '@occc/graphql';
import { PaginationInfo, usePaginatedQuery } from '@occc-app/hooks/usePaginatedQuery';

import { useLazyQuery } from '../utils';

export const useGetCampaignById = (campaignId: string) => {
  const response = useCampaignQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: {
      uuid: campaignId,
    },
  });

  const node = response.data?.campaigns.edges[0]?.node || null;

  return {
    ...response,
    data: node,
    loaded: !!node,
  };
};

type GetCampaignsWithCampaignComponentsArgs = {
  campaignFilter: CampaignFilter;
  offerFilter?: OfferFilter;
  pageSize?: number;
  baseOptions?: QueryHookOptions<
    CampaignsWithCampaignComponentsQuery,
    CampaignsWithCampaignComponentsQueryVariables
  >;
  paginationInfo?: PaginationInfo;
  onPageChange?: (info: PaginationInfo) => void;
};

export const useGetCampaignsWithCampaignComponents = ({
  campaignFilter,
  offerFilter,
  pageSize,
  baseOptions,
  paginationInfo,
  onPageChange,
}: GetCampaignsWithCampaignComponentsArgs) => {
  const response = usePaginatedQuery(
    useCampaignsWithCampaignComponentsQuery,
    'campaigns.pageInfo',
    pageSize,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      variables: {
        campaignFilter,
        offerFilter: {
          ...offerFilter,
          deleted: false,
        },
        skipOffers: !campaignFilter.search,
      },
      ...baseOptions,
    },
    paginationInfo,
    onPageChange,
  );

  return response;
};

const transformCampaignsQueryResponseToArray = (data: CampaignsQuery): CampaignFragment[] =>
  data?.campaigns?.edges.map((item) => item.node) || [];

export const useGetCampaignsLazy = (
  baseOptions?: LazyQueryHookOptions<CampaignsQuery, CampaignsQueryVariables>,
) => {
  const [executeQuery, response] = useLazyQuery(useCampaignsLazyQuery, baseOptions);

  return {
    executeQuery,
    response: {
      ...response,
      data: response.data ? transformCampaignsQueryResponseToArray(response.data) : response.data,
    },
  };
};

export const useGetCampaignsOffer = (campaignId: string, offerId: string) => {
  const campaignResponse = useCampaignQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      uuid: campaignId,
    },
  });

  const offerResponse = useOfferQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      filter: {
        campaignIdEquals: campaignId,
        idEquals: offerId,
      },
    },
  });

  const loading = campaignResponse.loading || offerResponse.loading;
  const error = campaignResponse.error || offerResponse.error;
  const campaign = campaignResponse.data?.campaigns.edges[0]?.node || null;
  const offer = offerResponse.data?.offers.edges[0]?.node || null;

  return {
    loading,
    error,
    data: {
      campaign,
      offer,
    },
  };
};

type GetCampaignComponentsArgs = {
  filter: OfferFilter;
  pageSize?: number;
  baseOptions?: QueryHookOptions<CampaignComponentsQuery, CampaignComponentsQueryVariables>;
  paginationInfo?: PaginationInfo;
  onPageChange?: (info: PaginationInfo) => void;
};

export const useGetCampaignComponents = ({
  filter,
  pageSize,
  baseOptions,
  paginationInfo,
  onPageChange,
}: GetCampaignComponentsArgs) => {
  const response = usePaginatedQuery(
    useCampaignComponentsQuery,
    'offers.pageInfo',
    pageSize,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      ...baseOptions,
      variables: {
        ...baseOptions?.variables,
        filter,
      },
      onCompleted: (data) => {
        if (baseOptions && baseOptions.onCompleted) {
          baseOptions.onCompleted(data);
        }
      },
    },
    paginationInfo,
    onPageChange,
  );

  return {
    ...response,
    data: response.data ? response.data.offers.edges.map((item) => item.node) : response.data,
  };
};
